/**
 * Program 12.1 UX4 — Bid strategy, win themes, team, workstreams (meta_json).
 */
import React, { useState } from 'react';
import { Button, Card, Input, List, Space, Typography, message } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { epcCommercialService } from '@/services/epcCommercial.service';

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

export type BidStrategyData = {
  winning_theme?: string;
  commercial_strategy?: string;
  technical_differentiators?: string;
  execution_strategy?: string;
  pricing_position?: string;
  win_themes?: string[];
  milestones?: Array<{ label: string; date?: string }>;
};

function parseStrategy(meta: unknown): BidStrategyData {
  if (!meta || typeof meta !== 'object') return {};
  const m = meta as Record<string, unknown>;
  const bs = (m.bid_strategy || m.bidStrategy) as BidStrategyData | undefined;
  return bs && typeof bs === 'object' ? bs : {};
}

export type EpcBidStrategyPanelProps = {
  tenderId: string;
  tender: Record<string, unknown>;
  workstreamPct?: Record<string, number>;
};

export const EpcBidStrategyPanel: React.FC<EpcBidStrategyPanelProps> = ({
  tenderId,
  tender,
  workstreamPct = {},
}) => {
  const qc = useQueryClient();
  const meta = (tender.meta_json || tender.metaJson || {}) as Record<string, unknown>;
  const initial = parseStrategy(meta);
  const [draft, setDraft] = useState<BidStrategyData>(initial);

  const saveM = useMutation({
    mutationFn: () =>
      epcCommercialService.patchTender(tenderId, {
        metaJson: { ...meta, bid_strategy: draft },
      }),
    onSuccess: () => {
      message.success('Bid strategy saved');
      qc.invalidateQueries({ queryKey: ['epc-tender-workspace', tenderId] });
    },
    onError: (e: Error) => message.error(e.message || 'Save failed'),
  });

  const workstreams = [
    { key: 'estimate', label: 'Estimate', pct: workstreamPct.estimate ?? 0 },
    { key: 'technical', label: 'Technical Proposal', pct: workstreamPct.technical ?? 0 },
    { key: 'commercial', label: 'Commercial Proposal', pct: workstreamPct.commercial ?? 0 },
    { key: 'documents', label: 'Documents', pct: workstreamPct.documents ?? 0 },
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Title level={5} style={{ margin: 0 }}>
        BID STRATEGY
      </Title>
      <Card size="small" title="Strategy summary">
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text type="secondary">Winning theme</Text>
          <TextArea
            rows={2}
            value={draft.winning_theme || ''}
            onChange={(e) => setDraft({ ...draft, winning_theme: e.target.value })}
          />
          <Text type="secondary">Commercial strategy</Text>
          <TextArea
            rows={2}
            value={draft.commercial_strategy || ''}
            onChange={(e) => setDraft({ ...draft, commercial_strategy: e.target.value })}
          />
          <Text type="secondary">Technical differentiators</Text>
          <TextArea
            rows={2}
            value={draft.technical_differentiators || ''}
            onChange={(e) => setDraft({ ...draft, technical_differentiators: e.target.value })}
          />
          <Text type="secondary">Win themes (one per line)</Text>
          <TextArea
            rows={3}
            value={(draft.win_themes || []).join('\n')}
            onChange={(e) =>
              setDraft({
                ...draft,
                win_themes: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean),
              })
            }
          />
          <Button type="primary" loading={saveM.isPending} onClick={() => saveM.mutate()}>
            Save strategy
          </Button>
        </Space>
      </Card>
      <Card size="small" title="Bid workstreams (deterministic readiness)">
        <List
          size="small"
          dataSource={workstreams}
          renderItem={(w) => (
            <List.Item>
              <Text>
                {w.label}: {w.pct}%
              </Text>
            </List.Item>
          )}
        />
      </Card>
      <Paragraph type="secondary" style={{ margin: 0, fontSize: 12 }}>
        Team assignments reuse platform user/owner fields — no parallel team engine.
      </Paragraph>
    </Space>
  );
};

export default EpcBidStrategyPanel;
